import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BrandMark } from '@/components/BrandLogo';
import { 
  LayoutDashboard, 
  CalendarDays, 
  Settings, 
  LogOut,
  Tablet,
  Menu,
  X,
  ChevronRight,
  PartyPopper,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard' },
  { path: '/admin/bookings', label: 'Bookings', icon: CalendarDays, permission: 'bookings' },
  { path: '/admin/events', label: 'Events', icon: PartyPopper, permission: 'bookings' },
  { path: '/admin/settings', label: 'Settings', icon: Settings, permission: 'settings' },
  { path: '/tablet', label: 'Tablet View', icon: Tablet, permission: 'tablet' },
];

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { restaurant, logout, switchUser, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userPermissions = user?.role === 'admin'
    ? ['dashboard', 'bookings', 'settings', 'tablet', 'users']
    : (user?.permissions || []);

  const handleLogout = async () => {
    try {
      await switchUser();
      navigate('/admin/pin-login');
    } catch (error) {
      console.error('Failed to log off to PIN screen:', error);
      logout();
      navigate('/admin/login');
    }
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={cn(
      "flex flex-col h-full bg-white border-r border-border/50",
      mobile ? "w-full" : "w-64"
    )}>
      {/* Logo */}
      <div className="p-6 border-b border-border/50">
        <Link to="/admin/dashboard" className="flex items-center gap-3">
          <BrandMark size="md" />
          <div>
            <h1 className="font-heading font-bold text-lg leading-tight">DineSpot</h1>
            <p className="text-xs text-muted-foreground truncate max-w-[140px]">
              {restaurant?.name || 'Restaurant'}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          {navItems.filter((item) => userPermissions.includes(item.permission)).map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path + '/'));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive 
                    ? "bg-primary text-primary-foreground" 
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
                {item.path === '/tablet' && (
                  <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
                )}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-4 border-t border-border/50">
        <div className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-red-800/80">Signed in as</p>
          <p className="mt-0.5 text-lg font-semibold leading-tight text-red-950 truncate">
            {user?.name || user?.email || 'Staff user'}
          </p>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5" />
          Log Off
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandMark size="sm" className="rounded-lg" />
            <span className="font-heading font-bold">{restaurant?.name || 'DineSpot'}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <div className="w-72 h-full" onClick={(e) => e.stopPropagation()}>
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 z-40">
        <Sidebar />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
